export interface FormulaEditorObjectSource {
    [key: string]: string[];
}

type objSourceType = FormulaEditorObjectSource | undefined;

export interface ITokenFields {
    type: TokenNamesEnum;
    content: string;
    index: string;
    prevElement?: TokenNamesEnum;
    hasError?: boolean;
    headError?: boolean;
    tailError?: boolean;
}

export interface ITokenResult {
    components: ITokenFields[];
    braceStack: string[];
    errorStack: Array<string>;
    sourceString?: string;
}

export enum TokenNamesEnum {
    DIGIT = 'DIGIT',
    VARIABLE = 'VARIABLE',
    SPACE = 'SPACE',
    LEFT_BRACE = 'LEFT_BRACE',
    RIGHT_BRACE = 'RIGHT_BRACE',
    MATH = 'MATH',
    OTHER = 'OTHER',
}

export enum ErrorMessages {
    BRACE_ERR = 'Непарная скобка',
    MATH_ERR = 'Пропущен знак',
    EXTRA_MATH_SIGN_ERR = 'Лишние математические знаки',
    VAR_ERR = 'Сущность отсутствует в Системе',
    VAR_DOT_ERR = 'Переменная отсутствует в Системе',
    OTHER_ERR = 'Некорректный знак',
}

export enum AutoCompleteEnum {
    EMPTY_SPACE = ' ',
    VARIABLE_CHAR = '@',
    VARIABLE_DIVIDER = '.',
    FIRST_LEVEL_OBJ = 'FIRST_LEVEL_OBJ',
    SECOND_LEVEL_OBJ = 'SECOND_LEVEL_OBJ',
    IMMEDIATELY_RUN = 'IMMEDIATELY_RUN',
    LEFT_BOUNDARY = '[',
    RIGHT_BOUNDARY = ']',
    RIGHT_BRACE_CHAR = ')',
}

export const VALID_MATH = /(\+|-|\*|\/)/g;
export const FAKE_VARIABLE = '(.[a-zA-Z0-9]+)';
export const DIGITS = /^[0-9]+(,[0-9]*)?$/;
export const SPACES = /(\s+)/;
export const LEFT_BRACE = /\(/;
export const RIGHT_BRACE = /\)/;
export const VARIABLE_SHIELD = /(\[@.*?])/;
export const TOKEN_SHIELD_PATTERN = /[+\-*/()]|[^+\-*/()]+/g;
export const VARIABLE_DESERIALIZE_PATTERN = /\[@([^|]+)[^\]]*]/g;
export const STRIP_SHIELD = /\[|]/g;
export const ANY_CHARACTER = /./g;
export const NEW_LINE = /\n/g;
export const EDITOR_DEFAULT_ID = 'formulaEditorDefault';

export const getMaxDigitsError = (max: number) => `Длина целой части должна быть не более ${max} знаков`;
export const getMaxFractionalDigitsError = (max: number) => `Длина дробной части должна быть не более ${max} знаков`;

export const findEstimatedVariableBoundaries = (inputString: string, caretPosition: number) => {
    let leftBoundary = caretPosition;

    while (leftBoundary > 0 && inputString[leftBoundary] !== AutoCompleteEnum.LEFT_BOUNDARY) {
        leftBoundary--;
    }

    let rightBoundary = caretPosition - 1;

    while (rightBoundary < inputString.length && inputString[rightBoundary] !== AutoCompleteEnum.RIGHT_BOUNDARY) {
        rightBoundary++;
    }

    return {
        left: leftBoundary,
        right: rightBoundary + 1,
    };
};

export const findWordBoundaries = (inputString: string, caretPosition: number) => {
    let leftBoundary = caretPosition;

    while (leftBoundary > 0 && inputString[leftBoundary] !== AutoCompleteEnum.VARIABLE_CHAR) {
        leftBoundary--;
    }

    let rightBoundary = caretPosition;

    while (
        rightBoundary < inputString.length &&
        inputString[rightBoundary] !== AutoCompleteEnum.EMPTY_SPACE &&
        inputString[rightBoundary] !== AutoCompleteEnum.RIGHT_BRACE_CHAR &&
        inputString[leftBoundary] !== AutoCompleteEnum.LEFT_BOUNDARY &&
        !ANY_CHARACTER.test(inputString[rightBoundary]) &&
        !NEW_LINE.test(inputString[rightBoundary])
    ) {
        rightBoundary++;
    }

    return {
        left: leftBoundary,
        right: rightBoundary,
    };
};

const getPreviousToken = (componentArr: ITokenFields[]): ITokenFields => componentArr[componentArr.length - 1] ?? {};
const isPrevTokenNotMath = (prevToken: ITokenFields) =>
    prevToken &&
    (prevToken.prevElement === TokenNamesEnum.DIGIT ||
        prevToken.prevElement === TokenNamesEnum.VARIABLE ||
        prevToken.prevElement === TokenNamesEnum.RIGHT_BRACE ||
        prevToken.type === TokenNamesEnum.DIGIT ||
        prevToken.type === TokenNamesEnum.VARIABLE ||
        prevToken.type === TokenNamesEnum.RIGHT_BRACE);
const isPrevTokenNotDigitOrVar = (prevToken: ITokenFields) =>
    prevToken.type !== TokenNamesEnum.DIGIT && prevToken.type !== TokenNamesEnum.VARIABLE;
const isErrorStackHasNotErrorType = (stack: Array<string>, type: string): boolean => !stack.includes(type);
const isCompositeToken = (token: string) => token.length > 1 && !!token.split(SPACES).filter((p) => p.length).length;
const isTokenHasUsableLength = (token: string) => token.trim().length;
const isTokenStartsWithVarChar = (token: string) => token.trim().startsWith(AutoCompleteEnum.VARIABLE_CHAR);

export const getStringByTokens = ({
    sourceString,
    validObjectKeys,
    maxIntegerPartLength,
    maxFractionalPartLength,
}: {
    sourceString: string;
    validObjectKeys: Array<string>;
    maxIntegerPartLength?: number;
    maxFractionalPartLength?: number;
}): ITokenResult => {
    const fakeVariableObjRegExp = validObjectKeys.map((key) => `@${key}${FAKE_VARIABLE}|@${key}`).join('|');

    const checkIntegerPartLength = (digits: string) => {
        if (!maxIntegerPartLength) {
            return false;
        }
        const beforeComma = digits.split(',')[0];

        return beforeComma.length > maxIntegerPartLength;
    };

    const checkFractionalPartLength = (digits: string) => {
        if (!maxFractionalPartLength) {
            return false;
        }
        const afterComma = digits.split(',')[1];

        return afterComma ? afterComma.length > maxFractionalPartLength : false;
    };

    return sourceString?.split(VARIABLE_SHIELD).reduce(
        (acc: ITokenResult, token, idx, fullString) => {
            if (VARIABLE_SHIELD.test(token)) {
                const prevComponent = getPreviousToken(acc.components);

                if (isPrevTokenNotMath(prevComponent)) {
                    if (isPrevTokenNotDigitOrVar(prevComponent)) {
                        acc.components[acc.components.length - 1] = {
                            ...prevComponent,
                            hasError: true,
                        };

                        acc.components.push({
                            type: TokenNamesEnum.VARIABLE,
                            content: token,
                            index: `${idx}`,
                        });
                    } else {
                        acc.components.push({
                            type: TokenNamesEnum.VARIABLE,
                            content: token,
                            index: `${idx}`,
                            hasError: true,
                            headError: true,
                        });
                    }

                    if (isErrorStackHasNotErrorType(acc.errorStack, ErrorMessages.MATH_ERR)) {
                        acc.errorStack.push(ErrorMessages.MATH_ERR);
                    }
                } else {
                    acc.components.push({
                        type: TokenNamesEnum.VARIABLE,
                        content: token,
                        index: `${idx}`,
                    });
                }

                return acc;
            }

            const stringWithFakeVariables = fakeVariableObjRegExp
                ? token.replaceAll(new RegExp(fakeVariableObjRegExp, 'g'), '[$&]').split(VARIABLE_SHIELD)
                : token.split(VARIABLE_SHIELD);

            stringWithFakeVariables.forEach((value, valueIdx) => {
                if (VARIABLE_SHIELD.test(value)) {
                    const strippedValue = value.replaceAll(STRIP_SHIELD, '');

                    acc.components.push({
                        type: TokenNamesEnum.VARIABLE,
                        content: strippedValue,
                        index: `${idx}_fake_${valueIdx}`,
                        hasError: true,
                    });

                    const varWithErr = strippedValue.split('.')[1] ?? '';

                    if (isErrorStackHasNotErrorType(acc.errorStack, `${varWithErr} - ${ErrorMessages.VAR_DOT_ERR}`)) {
                        acc.errorStack.push(`${varWithErr} - ${ErrorMessages.VAR_DOT_ERR}`);
                    }
                } else {
                    const restTokens = value.match(TOKEN_SHIELD_PATTERN);

                    restTokens?.forEach((element, innerIdx, tokensArr) => {
                        if (isCompositeToken(element)) {
                            element
                                .split(SPACES)
                                .filter((p) => p.length)
                                .forEach((inner, subIdx) => {
                                    if (!isTokenHasUsableLength(inner)) {
                                        const previousToken = getPreviousToken(acc.components);

                                        acc.components.push({
                                            type: TokenNamesEnum.SPACE,
                                            content: inner,
                                            prevElement: previousToken.type,
                                            index: `${idx}_inner_${innerIdx}_${subIdx}`,
                                        });
                                    } else if (isTokenStartsWithVarChar(inner)) {
                                        acc.components.push({
                                            type: TokenNamesEnum.VARIABLE,
                                            content: inner,
                                            index: `${idx}_inner_${innerIdx}_${subIdx}`,
                                            hasError: true,
                                        });

                                        if (
                                            isErrorStackHasNotErrorType(
                                                acc.errorStack,
                                                `${inner} - ${ErrorMessages.VAR_ERR}`,
                                            )
                                        ) {
                                            acc.errorStack.push(`${inner} - ${ErrorMessages.VAR_ERR}`);
                                        }
                                    } else {
                                        const previousToken = getPreviousToken(acc.components);

                                        if (isPrevTokenNotMath(previousToken)) {
                                            acc.components[acc.components.length - 1] = {
                                                ...previousToken,
                                                hasError: true,
                                            };

                                            if (isErrorStackHasNotErrorType(acc.errorStack, ErrorMessages.MATH_ERR)) {
                                                acc.errorStack.push(ErrorMessages.MATH_ERR);
                                            }
                                        }

                                        if (new RegExp(DIGITS).test(inner)) {
                                            const hasIntegerLengthError = checkIntegerPartLength(inner);
                                            const hasFractionalLengthError = checkFractionalPartLength(inner);
                                            const hasLengthError = hasIntegerLengthError || hasFractionalLengthError;

                                            acc.components.push({
                                                type: TokenNamesEnum.DIGIT,
                                                content: inner,
                                                index: `${idx}_inner_${innerIdx}_${subIdx}`,
                                                hasError: hasLengthError,
                                            });

                                            if (hasIntegerLengthError) {
                                                const msg = getMaxDigitsError(maxIntegerPartLength!);

                                                if (isErrorStackHasNotErrorType(acc.errorStack, msg)) {
                                                    acc.errorStack.push(msg);
                                                }
                                            }

                                            if (hasFractionalLengthError) {
                                                const msg = getMaxFractionalDigitsError(maxFractionalPartLength!);

                                                if (isErrorStackHasNotErrorType(acc.errorStack, msg)) {
                                                    acc.errorStack.push(msg);
                                                }
                                            }
                                        } else {
                                            acc.components.push({
                                                type: TokenNamesEnum.OTHER,
                                                content: inner,
                                                index: `${idx}_inner_${innerIdx}_${subIdx}`,
                                                hasError: true,
                                            });

                                            if (
                                                isErrorStackHasNotErrorType(
                                                    acc.errorStack,
                                                    `${element} - ${ErrorMessages.OTHER_ERR}`,
                                                )
                                            ) {
                                                acc.errorStack.push(`${inner} - ${ErrorMessages.OTHER_ERR}`);
                                            }
                                        }
                                    }
                                });
                        } else if (!isTokenHasUsableLength(element)) {
                            const previousToken = getPreviousToken(acc.components);

                            acc.components.push({
                                type: TokenNamesEnum.SPACE,
                                content: element,
                                prevElement: previousToken.type,
                                index: `${idx}_${innerIdx}`,
                            });
                        } else if (LEFT_BRACE.test(element)) {
                            const previousToken = getPreviousToken(acc.components);

                            if (isPrevTokenNotMath(previousToken)) {
                                acc.components[acc.components.length - 1] = {
                                    ...getPreviousToken(acc.components),
                                    hasError: true,
                                };

                                if (isErrorStackHasNotErrorType(acc.errorStack, ErrorMessages.MATH_ERR)) {
                                    acc.errorStack.push(ErrorMessages.MATH_ERR);
                                }
                            }

                            acc.components.push({
                                type: TokenNamesEnum.LEFT_BRACE,
                                content: element,
                                index: `${idx}_${innerIdx}`,
                            });

                            acc.braceStack.push(`${idx}_${innerIdx}`);
                        } else if (RIGHT_BRACE.test(element)) {
                            if (acc.braceStack.length) {
                                acc.braceStack.pop();

                                acc.components.push({
                                    type: TokenNamesEnum.RIGHT_BRACE,
                                    content: element,
                                    index: `${idx}_${innerIdx}`,
                                });
                            } else {
                                acc.components.push({
                                    type: TokenNamesEnum.RIGHT_BRACE,
                                    content: element,
                                    hasError: true,
                                    index: `${idx}_${innerIdx}`,
                                });

                                if (isErrorStackHasNotErrorType(acc.errorStack, ErrorMessages.BRACE_ERR)) {
                                    acc.errorStack.push(ErrorMessages.BRACE_ERR);
                                }
                            }
                        } else if (new RegExp(VALID_MATH).test(element)) {
                            const previousToken = innerIdx > 0 ? tokensArr[innerIdx - 1] : '';

                            if (new RegExp(VALID_MATH).test(previousToken)) {
                                acc.components.push({
                                    type: TokenNamesEnum.MATH,
                                    content: element,
                                    index: `${idx}_${innerIdx}`,
                                    hasError: true,
                                    tailError: true,
                                });

                                if (isErrorStackHasNotErrorType(acc.errorStack, ErrorMessages.EXTRA_MATH_SIGN_ERR)) {
                                    acc.errorStack.push(ErrorMessages.EXTRA_MATH_SIGN_ERR);
                                }
                            } else {
                                acc.components.push({
                                    type: TokenNamesEnum.MATH,
                                    content: element,
                                    index: `${idx}_${innerIdx}`,
                                });
                            }
                        } else if (DIGITS.test(element)) {
                            const previousToken = getPreviousToken(acc.components);

                            if (isPrevTokenNotMath(previousToken)) {
                                acc.components[acc.components.length - 1] = {
                                    ...previousToken,
                                    hasError: true,
                                    tailError: true,
                                };

                                if (isErrorStackHasNotErrorType(acc.errorStack, ErrorMessages.MATH_ERR)) {
                                    acc.errorStack.push(ErrorMessages.MATH_ERR);
                                }
                            }

                            const hasIntegerLengthError = checkIntegerPartLength(element);
                            const hasFractionalLengthError = checkFractionalPartLength(element);
                            const hasLengthError = hasIntegerLengthError || hasFractionalLengthError;

                            acc.components.push({
                                type: TokenNamesEnum.DIGIT,
                                content: element,
                                index: `${idx}_${innerIdx}`,
                                hasError: hasLengthError,
                            });

                            if (hasIntegerLengthError) {
                                const msg = getMaxDigitsError(maxIntegerPartLength!);

                                if (isErrorStackHasNotErrorType(acc.errorStack, msg)) {
                                    acc.errorStack.push(msg);
                                }
                            }

                            if (hasFractionalLengthError) {
                                const msg = getMaxFractionalDigitsError(maxFractionalPartLength!);

                                if (isErrorStackHasNotErrorType(acc.errorStack, msg)) {
                                    acc.errorStack.push(msg);
                                }
                            }
                        } else {
                            acc.components.push({
                                type: TokenNamesEnum.OTHER,
                                content: element,
                                index: `${idx}_other_${innerIdx}`,
                                hasError: true,
                            });

                            if (
                                isErrorStackHasNotErrorType(acc.errorStack, `${element} - ${ErrorMessages.OTHER_ERR}`)
                            ) {
                                acc.errorStack.push(`${element} - ${ErrorMessages.OTHER_ERR}`);
                            }
                        }
                    });
                }
            });

            if (idx === fullString.length - 1 && acc.braceStack.length) {
                acc.braceStack.forEach((index) => {
                    const errBraceIdx = acc.components.findIndex((item) => item.index === index);

                    acc.components[errBraceIdx] = {
                        ...acc.components[errBraceIdx],
                        hasError: true,
                    };
                });

                if (isErrorStackHasNotErrorType(acc.errorStack, ErrorMessages.BRACE_ERR)) {
                    acc.errorStack.push(ErrorMessages.BRACE_ERR);
                }
            }

            return acc;
        },
        {
            components: [],
            braceStack: [],
            errorStack: [],
        },
    );
};

export const buildInValidator = (
    objSource: FormulaEditorObjectSource = {},
    maxIntegerPartLength?: number,
    maxFractionalPartLength?: number,
) => {
    const validObjects = Object.keys(objSource);

    return (fieldValue: string) => {
        const errors = getStringByTokens({
            sourceString: fieldValue,
            validObjectKeys: validObjects,
            maxIntegerPartLength,
            maxFractionalPartLength,
        })?.errorStack;

        if (!errors?.length) {
            return undefined;
        }

        return errors.join('\n');
    };
};

export const getStringWithShieldsCreator = ({ objectSource = {} }: { objectSource: objSourceType }) => {
    const regexPattern = Object.keys(objectSource)
        .sort((a, b) => (b ?? '').length - (a ?? '').length)
        .map((key) => {
            if (objectSource[key]?.length) {
                return objectSource[key]
                    .sort((a, b) => b.length - a.length)
                    .map((nestedKey) => `@${key}.${nestedKey}`)
                    .join('|');
            }

            return `@${key}`;
        })
        .join('|');

    try {
        const regex = new RegExp(regexPattern.replace(/[-/\\^$*+?.(){}]/g, '\\$&'), 'g');

        return ({ fullStringValue }: { fullStringValue: string | null }) =>
            regexPattern ? fullStringValue?.replaceAll(STRIP_SHIELD, '')?.replace(regex, '[$&]') : fullStringValue;
    } catch (err) {
        console.error(err);
    }

    return ({ fullStringValue }: { fullStringValue: string | null }) => fullStringValue;
};
